/**
 * What the trainer did on THIS screen to the exercises outside the catalogue (board E, R8), as one
 * state and the named events that move it: the rows reopened with „Zmień", the writes on their way
 * and how each decision was reached, the quick adds that failed and why, which row is open, and the
 * draft the trainer's own last decision armed to open by itself.
 *
 * Every decision takes the same path, whichever control made it — a similar exercise, the search, a
 * skip, a quick add, the creator's save, the batch: `sent` when it leaves, then `landed` or
 * `refused`. Nothing a decision changes on screen is applied before the server has taken it: a
 * reopened row stays reopened, and its route is recorded, only once its write lands. A refused write
 * leaves the row exactly as it was.
 *
 * Pure: no React, no i18next. Keys are `decisionKey(turnId, name)`, so each draft version keeps its
 * own decisions.
 */

import { decisionKey } from '~/entities/ai-conversation';

/** How this screen reached a decision it sent, kept so the resolved row can say it. */
export type DecisionRoute =
  | { readonly kind: 'quick' }
  | { readonly kind: 'verified' }
  | { readonly kind: 'pick'; readonly target: string };

/** Which row the trainer has open: the first undecided one, a chosen one, or none. */
export type OpenRow =
  | { readonly kind: 'auto' }
  | { readonly kind: 'row'; readonly name: string }
  | { readonly kind: 'none' };

/**
 * Why a quick add did not reach the catalogue (board D): no answer at all, the server refusing the
 * proposal's values, or the server failing on its side.
 */
export type AddFailure = 'network' | 'rejected' | 'server';

/**
 * The failure an add met, from the HTTP status it was answered with; `null` is no answer. A timeout
 * (408) and a rate limit (429) are the server's to clear, like a 5xx; any other 4xx refused the
 * values themselves, and sending them again would be refused again.
 */
export function addFailureOf(status: number | null): AddFailure {
  if (status === null) return 'network';
  if (status >= 500 || status === 408 || status === 429) return 'server';
  return status >= 400 ? 'rejected' : 'server';
}

export interface ExerciseDecisionState {
  /** Decided names opened again with „Zmień": waiting, their decision standing until another lands. */
  readonly reopened: ReadonlySet<string>;
  /** How each decision this screen saw land was reached, so the resolved row can say it. */
  readonly routes: ReadonlyMap<string, DecisionRoute>;
  /** Decisions on their way, from the press to the server's answer, with the route they will record (`null`: a skip). */
  readonly writing: ReadonlyMap<string, DecisionRoute | null>;
  /** Quick adds that did not reach the catalogue, until another attempt or decision on the name. */
  readonly failures: ReadonlyMap<string, AddFailure>;
  /** The open row of each draft, by its build turn. */
  readonly open: ReadonlyMap<number, OpenRow>;
  /** The draft whose names the trainer decided on this screen since it last opened. */
  readonly armed: number | null;
}

export interface NamedRoute {
  readonly name: string;
  readonly route: DecisionRoute | null;
}

export type ExerciseDecisionEvent =
  /** The row's header pressed while it is waiting: open it, or close it when it is the open one. */
  | { readonly type: 'toggle'; readonly turnId: number; readonly name: string; readonly isOpen: boolean }
  /** „Zmień" on a decided row. */
  | { readonly type: 'reopen'; readonly turnId: number; readonly name: string }
  /** „Zostaw obecny wybór": the reopened decision stands again, unchanged. */
  | { readonly type: 'keep'; readonly turnId: number; readonly name: string }
  /** Decisions leave for the server (a quick add leaves at its catalogue write). */
  | { readonly type: 'sent'; readonly turnId: number; readonly decisions: readonly NamedRoute[] }
  /** The server took them. `arms`: a decision in the list can end in the draft opening by itself. */
  | { readonly type: 'landed'; readonly turnId: number; readonly names: readonly string[]; readonly arms: boolean }
  /** They were not taken, or never sent: the rows stand as they were before the press. */
  | { readonly type: 'refused'; readonly turnId: number; readonly names: readonly string[] }
  /** Quick adds the catalogue did not take. */
  | { readonly type: 'addFailed'; readonly turnId: number; readonly failures: readonly { readonly name: string; readonly failure: AddFailure }[] }
  /** The draft opened in the builder, by the trainer's press or by itself. */
  | { readonly type: 'opened' };

/** The state a screen starts from; `open` is the row the address asked for, if any. */
export function initialDecisionState(open: { readonly turnId: number; readonly name: string } | null): ExerciseDecisionState {
  return {
    reopened: new Set(),
    routes: new Map(),
    writing: new Map(),
    failures: new Map(),
    open: open === null ? new Map() : new Map([[open.turnId, { kind: 'row', name: open.name }]]),
    armed: null,
  };
}

function without<K, V>(map: ReadonlyMap<K, V>, keys: readonly K[]): ReadonlyMap<K, V> {
  const next = new Map(map);
  for (const key of keys) next.delete(key);
  return next;
}

function withoutKeys(set: ReadonlySet<string>, keys: readonly string[]): ReadonlySet<string> {
  const next = new Set(set);
  for (const key of keys) next.delete(key);
  return next;
}

export function exerciseDecisionReducer(state: ExerciseDecisionState, event: ExerciseDecisionEvent): ExerciseDecisionState {
  switch (event.type) {
    case 'toggle':
      return { ...state, open: new Map(state.open).set(event.turnId, event.isOpen ? { kind: 'none' } : { kind: 'row', name: event.name }) };
    case 'reopen':
      return {
        ...state,
        reopened: new Set(state.reopened).add(decisionKey(event.turnId, event.name)),
        open: new Map(state.open).set(event.turnId, { kind: 'row', name: event.name }),
      };
    case 'keep':
      return {
        ...state,
        reopened: withoutKeys(state.reopened, [decisionKey(event.turnId, event.name)]),
        open: new Map(state.open).set(event.turnId, { kind: 'auto' }),
      };
    case 'sent': {
      const writing = new Map(state.writing);
      for (const { name, route } of event.decisions) writing.set(decisionKey(event.turnId, name), route);
      // A new attempt takes the last failure's sentence down: if it fails again, it is said again.
      const keys = event.decisions.map(({ name }) => decisionKey(event.turnId, name));
      return { ...state, writing, failures: without(state.failures, keys) };
    }
    case 'landed': {
      const keys = event.names.map((name) => decisionKey(event.turnId, name));
      const routes = new Map(state.routes);
      for (const key of keys) {
        const route = state.writing.get(key) ?? null;
        if (route === null) routes.delete(key);
        else routes.set(key, route);
      }
      return {
        ...state,
        reopened: withoutKeys(state.reopened, keys),
        routes,
        writing: without(state.writing, keys),
        failures: without(state.failures, keys),
        armed: event.arms ? event.turnId : state.armed,
      };
    }
    case 'refused':
      return { ...state, writing: without(state.writing, event.names.map((name) => decisionKey(event.turnId, name))) };
    case 'addFailed': {
      const keys = event.failures.map(({ name }) => decisionKey(event.turnId, name));
      const failures = new Map(state.failures);
      for (const { name, failure } of event.failures) failures.set(decisionKey(event.turnId, name), failure);
      return { ...state, writing: without(state.writing, keys), failures };
    }
    case 'opened':
      return { ...state, armed: null };
  }
}

/** What one draft's list reads off the state, by name rather than by key. */
export interface DraftDecisionReading {
  readonly reopened: ReadonlySet<string>;
  readonly routes: ReadonlyMap<string, DecisionRoute>;
  readonly writing: ReadonlyMap<string, DecisionRoute | null>;
  readonly failures: ReadonlyMap<string, AddFailure>;
  readonly open: OpenRow;
}

export function draftDecisionReading(state: ExerciseDecisionState, turnId: number, names: readonly string[]): DraftDecisionReading {
  const byName = <V>(map: ReadonlyMap<string, V>): ReadonlyMap<string, V> =>
    new Map(names.flatMap((name) => (map.has(decisionKey(turnId, name)) ? [[name, map.get(decisionKey(turnId, name)) as V] as const] : [])));
  return {
    reopened: new Set(names.filter((name) => state.reopened.has(decisionKey(turnId, name)))),
    routes: byName(state.routes),
    writing: byName(state.writing),
    failures: byName(state.failures),
    open: state.open.get(turnId) ?? { kind: 'auto' },
  };
}
